import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Users, Building2, Tag, DollarSign, TrendingUp, TrendingDown, 
  PieChart, Plus, Filter, Download, AlertCircle
} from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeamCosts = () => {
  const { shouldShowData } = useDemoData();

  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', budget: '', department: '' });
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  useEffect(() => {
    fetchTeamCosts();
  }, [selectedPeriod]);

  const fetchTeamCosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/teams/costs?period=${selectedPeriod}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Error fetching team costs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeam = async () => {
    if (!newTeam.name || !newTeam.budget) {
      toast.error('Please fill all required fields');
      return;
    }
    toast.success(`Team "${newTeam.name}" added successfully`);
    setShowAddTeam(false);
    setNewTeam({ name: '', budget: '', department: '' });
    fetchTeamCosts();
  };

  const totalBudget = teams.reduce((sum, t) => sum + t.budget, 0);
  const totalSpent = teams.reduce((sum, t) => sum + t.spent, 0);
  const utilizationPercent = ((totalSpent / totalBudget) * 100).toFixed(1);

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'];

  const pieData = teams.map(t => ({ name: t.name, value: t.spent }));

  const barData = teams.map(t => ({
    name: t.name,
    budget: t.budget,
    spent: t.spent,
    remaining: t.budget - t.spent
  }));

  if (!shouldShowData) {
    return <EmptyDataState title="Team Costs" description="Connect your cloud account to see real data here." />;
  }


  return (
    <div className="space-y-6" data-testid="team-costs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
            <Users className="w-7 h-7 text-emerald-500" />
            Team Cost Allocation
          </h1>
          <p className="text-slate-400 dark:text-slate-400 mt-1">
            Track and manage costs by team and department
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setShowAddTeam(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Team
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Total Budget</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">${totalBudget.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-blue-900/30 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Total Spent</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">${totalSpent.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Budget Utilization</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{utilizationPercent}%</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <PieChart className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <Progress value={parseFloat(utilizationPercent)} className="mt-3 h-2" />
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Active Teams</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{teams.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-cyan-900/30 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-emerald-600 dark:text-cyan-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle>Cost Distribution by Team</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPie>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
              </RechartsPie>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle>Budget vs Actual Spending</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Legend />
                <Bar dataKey="budget" fill="#3b82f6" name="Budget" />
                <Bar dataKey="spent" fill="#10b981" name="Spent" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Team List */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle>Team Details</CardTitle>
          <CardDescription>Detailed breakdown of costs by team</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {teams.map((team, index) => {
              const utilization = ((team.spent / team.budget) * 100).toFixed(1);
              const isOverBudget = team.spent > team.budget;
              const isNearBudget = utilization > 80 && !isOverBudget;
              
              return (
                <div 
                  key={team.id} 
                  className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-emerald-500 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${COLORS[index % COLORS.length]}20` }}
                      >
                        <Users className="w-5 h-5" style={{ color: COLORS[index % COLORS.length] }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-white">{team.name}</h3>
                        <p className="text-sm text-slate-400">{team.department}</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-slate-400">Budget</p>
                        <p className="font-semibold text-slate-900 dark:text-white">${team.budget.toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-400">Spent</p>
                        <p className={`font-semibold ${isOverBudget ? 'text-red-600' : 'text-emerald-600'}`}>
                          ${team.spent.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right min-w-[80px]">
                        <p className="text-sm text-slate-400">Trend</p>
                        <div className={`flex items-center justify-end gap-1 font-semibold ${team.trend > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                          {team.trend > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                          {Math.abs(team.trend)}%
                        </div>
                      </div>
                      <div>
                        {isOverBudget && <Badge variant="destructive">Over Budget</Badge>}
                        {isNearBudget && <Badge variant="warning" className="bg-amber-100 text-amber-800">Near Limit</Badge>}
                        {!isOverBudget && !isNearBudget && <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">On Track</Badge>}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-400">Utilization</span>
                      <span className={isOverBudget ? 'text-red-600' : 'text-slate-700 dark:text-slate-300'}>{utilization}%</span>
                    </div>
                    <Progress 
                      value={Math.min(parseFloat(utilization), 100)} 
                      className={`h-2 ${isOverBudget ? '[&>div]:bg-red-500' : isNearBudget ? '[&>div]:bg-amber-500' : ''}`}
                    />
                  </div>
                  
                  <div className="mt-3 flex flex-wrap gap-2">
                    {team.services.map((service, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        <Tag className="w-3 h-3 mr-1" />
                        {service}
                      </Badge>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Add Team Modal */}
      {showAddTeam && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Add New Team</CardTitle>
              <CardDescription>Create a new team for cost tracking</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="team-name">Team Name *</Label>
                <Input
                  id="team-name"
                  value={newTeam.name}
                  onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                  placeholder="e.g., Engineering"
                />
              </div>
              <div>
                <Label htmlFor="team-department">Department</Label>
                <Select value={newTeam.department} onValueChange={(v) => setNewTeam({ ...newTeam, department: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Technology">Technology</SelectItem>
                    <SelectItem value="Business">Business</SelectItem>
                    <SelectItem value="Operations">Operations</SelectItem>
                    <SelectItem value="Marketing">Marketing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="team-budget">Monthly Budget *</Label>
                <Input
                  id="team-budget"
                  type="number"
                  value={newTeam.budget}
                  onChange={(e) => setNewTeam({ ...newTeam, budget: e.target.value })}
                  placeholder="e.g., 25000"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowAddTeam(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleAddTeam} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  Add Team
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TeamCosts;
